export type HostThemeMode = 'light' | 'dark';

export type HostTheme = {
  mode?: HostThemeMode;
  colors?: {
    primary?: string;
    secondary?: string;
    accent?: string;
    background?: string;
    surface?: string;
    text?: string;
    textSecondary?: string;
    border?: string;
  };
  borderRadius?: {
    md?: number;
  };
};

export type HostTenantBranding = {
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  backgroundColor?: string;
  fontFamily?: string;
  borderRadius?: number;
};

const readableSansFontFamily =
  'Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function normalizeHostFontFamily(value: unknown): string | undefined {
  if (!isNonEmptyString(value)) return undefined;
  const fontFamily = value.trim();
  if (/^system$/i.test(fontFamily)) return undefined;

  const compact = fontFamily.replace(/\s+/g, '').toLowerCase();
  if (compact === 'inter,sans-serif' || compact === '"inter",sans-serif' || compact === "'inter',sans-serif") {
    return readableSansFontFamily;
  }

  return fontFamily;
}

function toPx(value: unknown): string | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return `${value}px`;
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) return `${parsed}px`;
  }
  return undefined;
}

export function applyHostThemeToDocument(theme?: HostTheme): void {
  try {
    if (typeof document === 'undefined') return;
    if (!theme || typeof theme !== 'object') return;

    const root = document.documentElement;
    const colors = theme.colors || {};

    const mappings: Array<[string, unknown]> = [
      ['--host-primary', colors.primary],
      ['--host-secondary', colors.secondary],
      ['--host-accent', colors.accent],
      ['--host-bg', colors.background],
      ['--host-surface', colors.surface],
      ['--host-text', colors.text],
      ['--host-text-secondary', colors.textSecondary],
      ['--host-border', colors.border],
      ['--host-radius-md', toPx(theme.borderRadius?.md)],
    ];

    for (const [cssVar, raw] of mappings) {
      if (isNonEmptyString(raw)) {
        root.style.setProperty(cssVar, raw.trim());
      }
    }

    // Helpful for native controls (address bar tint on mobile).
    if (isNonEmptyString(colors.primary)) {
      const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
      if (meta) meta.content = colors.primary.trim();
    }

    // Provide a hook for CSS selectors.
    if (theme.mode === 'dark') {
      root.dataset.hostThemeMode = 'dark';
    } else if (theme.mode === 'light') {
      root.dataset.hostThemeMode = 'light';
    }
  } catch {
    // ignore
  }
}

export function applyHostTenantBrandingToDocument(branding?: HostTenantBranding): void {
  try {
    if (typeof document === 'undefined') return;
    if (!branding || typeof branding !== 'object') return;

    const root = document.documentElement;
    const normalizedFontFamily = normalizeHostFontFamily(branding.fontFamily);

    const mappings: Array<[string, unknown]> = [
      ['--host-primary', branding.primaryColor],
      ['--host-secondary', branding.secondaryColor],
      ['--host-accent', branding.accentColor],
      ['--host-bg', branding.backgroundColor],
      ['--host-font', normalizedFontFamily],
      ['--host-radius-md', toPx(branding.borderRadius)],
    ];

    for (const [cssVar, raw] of mappings) {
      if (isNonEmptyString(raw)) {
        root.style.setProperty(cssVar, raw.trim());
      }
    }

    // Use a host font only when it is explicit and readable.
    if (normalizedFontFamily) {
      root.style.setProperty('font-family', normalizedFontFamily);
    }

    // Helpful for native controls (address bar tint on mobile).
    if (isNonEmptyString(branding.primaryColor)) {
      const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
      if (meta) meta.content = branding.primaryColor.trim();
    }
  } catch {
    // ignore
  }
}

export function extractHostThemeFromInitPayload(payload: unknown): HostTheme | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const anyPayload = payload as any;
  const theme = anyPayload.theme;
  if (!theme || typeof theme !== 'object') return undefined;

  // We only return the fields we understand.
  const mode = theme.mode === 'dark' ? 'dark' : theme.mode === 'light' ? 'light' : undefined;
  const colors = theme.colors && typeof theme.colors === 'object' ? theme.colors : undefined;
  const borderRadius = theme.borderRadius && typeof theme.borderRadius === 'object' ? theme.borderRadius : undefined;

  return {
    mode,
    colors: colors
      ? {
          primary: colors.primary,
          secondary: colors.secondary,
          accent: colors.accent,
          background: colors.background,
          surface: colors.surface,
          text: colors.text,
          textSecondary: colors.textSecondary,
          border: colors.border,
        }
      : undefined,
    borderRadius: borderRadius ? { md: borderRadius.md } : undefined,
  };
}

export function extractHostTenantBrandingFromInitPayload(payload: unknown): HostTenantBranding | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const anyPayload = payload as any;

  const tenantConfig = anyPayload.tenantConfig;
  if (!tenantConfig || typeof tenantConfig !== 'object') return undefined;

  const branding = tenantConfig.branding;
  if (!branding || typeof branding !== 'object') return undefined;

  return {
    primaryColor: branding.primaryColor,
    secondaryColor: branding.secondaryColor,
    accentColor: branding.accentColor,
    backgroundColor: branding.backgroundColor,
    fontFamily: branding.fontFamily,
    borderRadius: branding.borderRadius,
  };
}
